import { useState, useEffect, useRef } from 'react';
import { X, Gift, BadgePercent } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

// Mostra uma única vez por sessão (sessionStorage = limpa quando o navegador/aba fecha)
const SESSION_KEY = 'first_purchase_popup_shown_v3';
// Delay antes de exibir automaticamente (segundos)
const AUTO_SHOW_DELAY_SECONDS = 15;
// Tempo mínimo no site antes do exit-intent disparar (segundos)
// Evita parecer agressivo demais — o visitante precisa ter um mínimo de engajamento
const MIN_TIME_FOR_EXIT_INTENT_SECONDS = 8;

const wasShownThisSession = () => {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

const markAsShown = () => {
  try { sessionStorage.setItem(SESSION_KEY, '1'); } catch {}
};

export const FirstPurchasePopup = () => {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  const mountedAtRef = useRef(Date.now());
  const triggeredRef = useRef(false);

  const tryShow = () => {
    if (triggeredRef.current) return;
    if (wasShownThisSession()) return;
    triggeredRef.current = true;
    markAsShown();
    setShow(true);
  };

  useEffect(() => {
    // Não dispara em páginas sensíveis (admin / login / carrinho / checkout)
    const path = location.pathname || '';
    if (
      path.startsWith('/admin') ||
      path.startsWith('/entrar') ||
      path.startsWith('/carrinho') ||
      path.startsWith('/minha-conta')
    ) {
      return;
    }
    // Já apareceu nesta sessão? Não agenda nada
    if (wasShownThisSession()) return;

    // 1) AUTO-SHOW: depois de 15s no site
    const autoTimer = setTimeout(tryShow, AUTO_SHOW_DELAY_SECONDS * 1000);

    // 2) EXIT-INTENT (Desktop): mouse saindo pelo topo da viewport
    const onMouseLeave = (e) => {
      // só ativa se passou tempo mínimo (evita disparo no início)
      if (Date.now() - mountedAtRef.current < MIN_TIME_FOR_EXIT_INTENT_SECONDS * 1000) return;
      if (e.clientY <= 0) tryShow();
    };

    // 3) EXIT-INTENT (Mobile): troca de aba ou minimiza navegador
    const onVisibilityChange = () => {
      if (Date.now() - mountedAtRef.current < MIN_TIME_FOR_EXIT_INTENT_SECONDS * 1000) return;
      if (document.visibilityState === 'hidden') tryShow();
    };

    document.addEventListener('mouseleave', onMouseLeave);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(autoTimer);
      document.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleDismiss = () => {
    setShow(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText('BEMVINDO10');
    setCopied(true);
    toast.success('Cupom copiado!');
    setTimeout(() => handleDismiss(), 1500);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="first-purchase-popup"
      onClick={handleDismiss}
      style={{ animation: 'fadeIn 0.3s ease-out' }}
    >
      <div
        className="relative w-full max-w-sm overflow-hidden rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'slideUp 0.4s ease-out' }}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/40 rounded-full transition-colors"
          data-testid="popup-close"
          aria-label="Fechar"
        >
          <X className="h-5 w-5 text-white" strokeWidth={2.5} />
        </button>

        {/* Top - Dark premium */}
        <div className="bg-gradient-to-br from-[#0a0a0a] via-[#161616] to-[#0a0a0a] px-6 pt-9 pb-7 text-center">
          <p
            className="text-[#C4A35A] text-[10px] font-semibold uppercase tracking-[0.35em] mb-2"
            style={{ fontFamily: "var(--font-body)" }}
          >
            Boas-vindas Premium
          </p>
          <p
            className="text-white text-4xl font-bold leading-none mb-2"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            2 brindes pra você
          </p>
          <p className="text-white/60 text-xs">
            Aproveite a partir da sua primeira compra
          </p>
        </div>

        {/* Body - white */}
        <div className="bg-white px-6 py-6 space-y-4">
          {/* Bonus 1 - Coupon */}
          <div className="border border-gray-200 rounded-xl p-4 hover:border-[#0a0a0a] transition-colors" data-testid="popup-bonus-coupon">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-[#0a0a0a] flex items-center justify-center flex-shrink-0">
                <BadgePercent className="h-4 w-4 text-[#C4A35A]" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Brinde 1</p>
                <p className="text-base font-bold text-gray-900 leading-tight">10% OFF na 1ª compra</p>
              </div>
            </div>
            <button
              onClick={handleCopy}
              className="w-full border-2 border-dashed border-[#0a0a0a] py-2.5 px-3 hover:bg-[#0a0a0a] hover:text-white transition-colors group flex items-center justify-center gap-2 rounded-lg"
              data-testid="popup-copy-coupon"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-[#0a0a0a] group-hover:text-white transition-colors flex-shrink-0">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <span
                className="text-lg font-bold tracking-[0.15em] text-[#0a0a0a] group-hover:text-white transition-colors"
                style={{ fontFamily: "var(--font-body)" }}
              >
                {copied ? 'COPIADO!' : 'BEMVINDO10'}
              </span>
            </button>
            <p className="text-[10px] text-gray-400 text-center mt-1.5">Toque para copiar</p>
          </div>

          {/* Bonus 2 - Signup R$20 */}
          <Link
            to="/entrar"
            onClick={handleDismiss}
            className="block border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white rounded-xl p-4 hover:border-emerald-600 hover:shadow-md transition-all group"
            data-testid="popup-bonus-signup"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                <Gift className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-emerald-700 uppercase tracking-wider font-semibold">Brinde 2</p>
                <p className="text-base font-bold text-gray-900 leading-tight">
                  R$ 20,00 grátis na conta
                </p>
                <p className="text-[11px] text-gray-600 mt-0.5">
                  Crie sua conta e ganhe R$ 20 de cashback para usar na primeira compra.
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-center gap-1.5 py-2 bg-emerald-600 group-hover:bg-emerald-700 transition-colors rounded-lg text-white text-xs font-semibold">
              Criar minha conta grátis
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
              </svg>
            </div>
          </Link>

          <p className="text-[10px] text-gray-400 text-center pt-1">
            Promoções podem ser combinadas. Válido por tempo limitado.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default FirstPurchasePopup;
